import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  useApi,
  ApiError,
  type Whanau,
  type WhanauListResponse,
} from "../api/client";
import "./WhanauDirectory.css";

/**
 * Phase 1 first cut — lists the whānau the signed-in kaimahi has access to
 * via their service assignment. Calls GET /api/v1/whanau on mount; renders
 * the result with client-side search and sort.
 *
 * All filtering / sorting is in-memory over the fetched rows — for the
 * Phase 1 caseload sizes (low hundreds) this is fast and avoids a network
 * round-trip on every keystroke. Phase 2 will revisit if the caseload
 * scales past a few thousand rows per kaimahi.
 *
 * Loading, error, no-results, and empty states each have their own panel.
 */

type SortKey =
  | "registered-desc"
  | "registered-asc"
  | "name-asc"
  | "name-desc"
  | "engagement";

// Engagement-state ordering for the 'engagement' sort: active first, then
// intake (newly opened cases), paused, then exited / declined at the end.
const ENGAGEMENT_PRIORITY: Readonly<Record<string, number>> = {
  active: 1,
  intake: 2,
  paused: 3,
  exited: 4,
  declined: 5,
};

const COUNT_ORDER: readonly string[] = [
  "active",
  "intake",
  "paused",
  "exited",
  "declined",
];

export function WhanauDirectory() {
  const { t } = useTranslation();
  const callApi = useApi();

  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "ok"; data: WhanauListResponse }
    | { kind: "error"; message: string; status?: number }
  >({ kind: "loading" });

  const [searchQuery, setSearchQuery] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("registered-desc");

  useEffect(() => {
    let active = true;

    callApi<WhanauListResponse>("/whanau")
      .then((data) => {
        if (active) setState({ kind: "ok", data });
      })
      .catch((err: unknown) => {
        if (!active) return;
        if (err instanceof ApiError) {
          setState({
            kind: "error",
            status: err.status,
            message: err.message,
          });
        } else {
          const message = err instanceof Error ? err.message : "Unknown error";
          setState({ kind: "error", message });
        }
      });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Per-engagement-state counts off the unfiltered list — gives a stable
  // sense of the whole caseload regardless of search.
  const engagementCounts = useMemo(() => {
    if (state.kind !== "ok") return new Map<string, number>();
    const m = new Map<string, number>();
    for (const w of state.data.rows) {
      m.set(w.engagement_state, (m.get(w.engagement_state) ?? 0) + 1);
    }
    return m;
  }, [state]);

  // Filter + sort.
  const visibleRows = useMemo(() => {
    if (state.kind !== "ok") return [] as Whanau[];
    const query = searchQuery.trim().toLowerCase();
    const filtered = query
      ? state.data.rows.filter((w) =>
          w.display_name.toLowerCase().includes(query),
        )
      : state.data.rows;

    const sorted = [...filtered].sort((a, b) => {
      switch (sortKey) {
        case "registered-desc":
          return b.registered_at.localeCompare(a.registered_at);
        case "registered-asc":
          return a.registered_at.localeCompare(b.registered_at);
        case "name-asc":
          return a.display_name.localeCompare(b.display_name, undefined, {
            sensitivity: "base",
          });
        case "name-desc":
          return b.display_name.localeCompare(a.display_name, undefined, {
            sensitivity: "base",
          });
        case "engagement": {
          const pa = ENGAGEMENT_PRIORITY[a.engagement_state] ?? 99;
          const pb = ENGAGEMENT_PRIORITY[b.engagement_state] ?? 99;
          if (pa !== pb) return pa - pb;
          // Tie-break by registered_at desc.
          return b.registered_at.localeCompare(a.registered_at);
        }
      }
    });
    return sorted;
  }, [state, searchQuery, sortKey]);

  const totalCount = state.kind === "ok" ? state.data.count : undefined;
  const filteredCount = visibleRows.length;
  const isFiltering = state.kind === "ok" && searchQuery.trim().length > 0;

  return (
    <section
      className="whanau-directory"
      aria-labelledby="whanau-directory-title"
    >
      <header className="whanau-directory__header">
        <h2 id="whanau-directory-title" className="whanau-directory__title">
          {t("directory.title")}
        </h2>
        {totalCount !== undefined && (
          <span className="whanau-directory__count">
            {isFiltering
              ? t("directory.countFiltered", {
                  shown: filteredCount,
                  total: totalCount,
                })
              : t("directory.count", { count: totalCount })}
          </span>
        )}
        <Link to="/directory/closed" className="whanau-directory__closed-link">
          {t("directory.viewClosed")}
        </Link>
      </header>

      {/* Engagement-state count chips — stable summary of the whole caseload. */}
      {state.kind === "ok" && state.data.count > 0 && (
        <div className="whanau-directory__counts" role="list">
          {COUNT_ORDER.filter((s) => (engagementCounts.get(s) ?? 0) > 0).map(
            (s) => (
              <span
                key={s}
                role="listitem"
                className={`whanau-directory__chip whanau-directory__chip--${s}`}
              >
                <span className="whanau-directory__chip-label">{s}</span>
                <span className="whanau-directory__chip-count">
                  {engagementCounts.get(s) ?? 0}
                </span>
              </span>
            ),
          )}
        </div>
      )}

      {/* Search + sort controls. */}
      {state.kind === "ok" && state.data.count > 0 && (
        <div className="whanau-directory__controls" role="group">
          <div className="whanau-directory__search">
            <label
              htmlFor="whanau-search"
              className="whanau-directory__search-label"
            >
              {t("directory.searchLabel")}
            </label>
            <input
              id="whanau-search"
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("directory.searchPlaceholder")}
              className="whanau-directory__search-input"
              autoComplete="off"
              spellCheck={false}
            />
          </div>

          <div className="whanau-directory__sort">
            <label
              htmlFor="whanau-sort"
              className="whanau-directory__sort-label"
            >
              {t("directory.sortLabel")}
            </label>
            <select
              id="whanau-sort"
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey)}
              className="whanau-directory__sort-select"
            >
              <option value="registered-desc">
                {t("directory.sort.registeredDesc")}
              </option>
              <option value="registered-asc">
                {t("directory.sort.registeredAsc")}
              </option>
              <option value="name-asc">{t("directory.sort.nameAsc")}</option>
              <option value="name-desc">{t("directory.sort.nameDesc")}</option>
              <option value="engagement">
                {t("directory.sort.engagement")}
              </option>
            </select>
          </div>
        </div>
      )}

      {state.kind === "loading" && (
        <p className="whanau-directory__status">{t("directory.loading")}</p>
      )}

      {state.kind === "error" && (
        <p className="whanau-directory__status whanau-directory__status--error">
          {state.status
            ? `${String(state.status)} — ${state.message}`
            : state.message}
        </p>
      )}

      {state.kind === "ok" && state.data.count === 0 && (
        <p className="whanau-directory__status">{t("directory.empty")}</p>
      )}

      {state.kind === "ok" && state.data.count > 0 && filteredCount === 0 && (
        <p className="whanau-directory__status">
          {t("directory.noMatches", { query: searchQuery.trim() })}
        </p>
      )}

      {state.kind === "ok" && filteredCount > 0 && (
        <ul className="whanau-directory__list">
          {visibleRows.map((w: Whanau) => (
            <li key={w.whanau_id}>
              <Link
                to={`/whanau/${String(w.whanau_id)}`}
                className="whanau-row"
                aria-label={t("directory.openProfile", {
                  name: w.display_name,
                })}
              >
                <div className="whanau-row__main">
                  <p className="whanau-row__name">{w.display_name}</p>
                  <p className="whanau-row__meta">
                    <span className="whanau-row__service">{w.service_id}</span>
                    <span aria-hidden="true"> · </span>
                    <span
                      className={`whanau-row__engagement whanau-row__engagement--${w.engagement_state}`}
                    >
                      {w.engagement_state}
                    </span>
                    <span aria-hidden="true"> · </span>
                    <span className="whanau-row__keyworker-count">
                      {t("directory.keyworkerCount", {
                        count: w.current_keyworker_count,
                      })}
                    </span>
                  </p>
                </div>
                {w.primary_location && (
                  <p className="whanau-row__location">{w.primary_location}</p>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default WhanauDirectory;
